// Core business logic. Pure, testable, framework-independent.

import type { Appointment, FinderConfig } from './types';

/** Number of slot columns in the matrix for a given config. */
export function slotCount(config: FinderConfig): number {
  return Math.floor((config.dayEndMinutes - config.dayStartMinutes) / config.slotSizeMinutes);
}

/** Returns the minutes-since-midnight of the slot at column index. */
export function slotStartMinutes(config: FinderConfig, columnIndex: number): number {
  return config.dayStartMinutes + columnIndex * config.slotSizeMinutes;
}

export type DayZone = 'night' | 'yellow' | 'yg' | 'green';

/** Maps a minute-of-day to one of four day-phase zones. The boundaries
 *  match the time-picker strip so the matrix can mirror its colours.
 *
 *  0–7   → night (orange)
 *  7–8   → yellow      (Aufwachen)
 *  8–9   → yellow-green (Übergang)
 *  9–12  → green       (Vormittags-Kernzeit)
 *  12–13 → yellow      (Mittagspause)
 *  13–14 → yellow-green
 *  14–17 → green       (Nachmittags-Kernzeit)
 *  17–18 → yellow-green
 *  18–24 → night
 */
export function zoneForMinutes(minutes: number): DayZone {
  const h = minutes / 60;
  if (h < 7)  return 'night';
  if (h < 8)  return 'yellow';
  if (h < 9)  return 'yg';
  if (h < 12) return 'green';
  if (h < 13) return 'yellow';
  if (h < 14) return 'yg';
  if (h < 17) return 'green';
  if (h < 18) return 'yg';
  return 'night';
}

/** Returns true if a candidate appointment fits within the configured day window. */
export function fitsInDayWindow(config: FinderConfig, appointment: Appointment): boolean {
  return (
    appointment.startMinutes >= config.dayStartMinutes && appointment.endMinutes <= config.dayEndMinutes
  );
}

/**
 * Returns true if two appointments overlap on the same day AND lane.
 * Appointments on different lanes are explicitly allowed to share a time range —
 * that is the whole point of the lane mechanism.
 */
export function appointmentsOverlap(a: Appointment, b: Appointment): boolean {
  if (a.date !== b.date) return false;
  if (a.lane !== b.lane) return false;
  return a.startMinutes < b.endMinutes && a.endMinutes > b.startMinutes;
}

/** Find the first existing appointment that conflicts with candidate (by date + lane + overlap). */
export function findConflict(existing: Appointment[], candidate: Appointment): Appointment | null {
  for (const appt of existing) {
    if (appt.id === candidate.id) continue;
    if (appointmentsOverlap(appt, candidate)) return appt;
  }
  return null;
}

/**
 * Returns the smallest lane number ≥ 0 that has no appointment overlapping
 * [startMinutes, endMinutes) on the given date.
 */
export function nextFreeLane(
  existing: Appointment[],
  date: string,
  startMinutes: number,
  endMinutes: number,
): number {
  const occupied = new Set<number>();
  for (const a of existing) {
    if (a.date !== date) continue;
    if (a.startMinutes < endMinutes && a.endMinutes > startMinutes) {
      occupied.add(a.lane);
    }
  }
  let lane = 0;
  while (occupied.has(lane)) lane++;
  return lane;
}

/** Find the appointment covering a specific (date, col, lane) cell, if any. */
export function appointmentAtCell(
  existing: Appointment[],
  date: string,
  cellStart: number,
  cellEnd: number,
  lane: number,
): Appointment | null {
  for (const a of existing) {
    if (a.date !== date) continue;
    if (a.lane !== lane) continue;
    if (a.startMinutes < cellEnd && a.endMinutes > cellStart) return a;
  }
  return null;
}

/** How many lanes are needed to display all appointments of this day. */
export function laneCountForDate(existing: Appointment[], date: string): number {
  let maxLane = -1;
  for (const a of existing) {
    if (a.date !== date) continue;
    if (a.lane > maxLane) maxLane = a.lane;
  }
  return Math.max(1, maxLane + 1);
}

/** Build a candidate appointment from a cell click. */
export function candidateFromCell(
  config: FinderConfig,
  date: string,
  columnIndex: number,
  lane = 0,
): Appointment {
  const startMinutes = slotStartMinutes(config, columnIndex);
  return {
    id: makeId(),
    date,
    lane,
    startMinutes,
    endMinutes: startMinutes + config.defaultDurationMinutes,
  };
}

/** Simple id generator — sufficient for client-side uniqueness. */
export function makeId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

/** Sort appointments by date then start time then lane. */
export function sortAppointments(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
    return a.lane - b.lane;
  });
}

/** Default configuration: today + 4 weeks, weekdays, 08:00–18:00, 30 min raster, 60 min duration.
 *
 *  Why "today" and not "first of month": users typically schedule into the
 *  future, not retroactively. Starting in the past wastes vertical space and
 *  hides events behind already-elapsed days. */
export function defaultConfig(today: Date = new Date()): FinderConfig {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + 28);
  return {
    rangeStart: toISODateLocal(start),
    rangeEnd: toISODateLocal(end),
    allowedWeekdays: [1, 2, 3, 4, 5],
    dayStartMinutes: 8 * 60,
    dayEndMinutes: 18 * 60,
    slotSizeMinutes: 15,
    defaultDurationMinutes: 60,
    holidayCountries: ['AT'],
  };
}

function toISODateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
